import React, { useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import styles from './PricingBreakdownTable.module.scss';
const buildFeatures = () => [
  {
    category: 'Core Features',
    items: [
      {
        title: 'Job applications',
        description:
          'The ability to apply to jobs posted on Twine. Starter accounts have limits on which jobs they can apply to.',
        starter: '1 per day',
        pro: '50 per month',
        premium: 'Unlimited',
      },
      {
        title: 'Visibility to clients',
        description:
          'How you appear in search results to clients looking for freelancers.',
        starter: 'Standard',
        pro: 'Increased',
        premium: 'Premium',
      },
      {
        title: 'Job alerts',
        description: 'Instant notifications of new relevant jobs on Twine.',
        starter: true,
        pro: true,
        premium: true,
      },
      {
        title: 'Portfolio',
        description:
          'Display your work on your portfolio to be viewed by clients.',
        starter: true,
        pro: true,
        premium: true,
      },
      {
        title: 'AI cover letter writer',
        description:
          'A tool to help you write compelling cover letters, pitches, and proposals.',
        starter: '3 per day',
        pro: '50 per day',
        premium: '50 per day',
      },
    ],
  },
  {
    category: 'Payments',
    items: [
      {
        title: 'Transaction fees',
        description:
          'Transaction fees are a percentage of your earnings and depend on what payout method you choose. Pro and Premium members enjoy zero transaction fees.',
        starter: 'Up to 6%',
        pro: '0%',
        premium: '0%',
      },
      {
        title: 'International Payouts',
        description:
          'International payouts are available to all members via several common payment platforms.',
        starter: true,
        pro: true,
        premium: true,
      },
      {
        title: 'Twine Vault',
        description:
          'Use Twine Vault, an escrow-like service, for added security when working on a project with a client.',
        starter: true,
        pro: true,
        premium: true,
      },
      {
        title: 'Off-platform payments',
        description:
          'Directly invoice clients to use payment platforms of your choice. We do not restrict you.',
        starter: true,
        pro: true,
        premium: true,
      },
    ],
  },
  {
    category: 'Ari',
    items: [
      {
        title: 'Ari AI job agent',
        description:
          'An AI agent that finds freelance and contract jobs across the web, matched to your Twine portfolio.',
        starter: true,
        pro: true,
        premium: true,
      },
      {
        title: 'Searches',
        description:
          'How many times you can launch Ari to find new job matches. Free searches reset every Monday.',
        starter: '3 per week',
        pro: 'Unlimited',
        premium: 'Unlimited',
      },
      {
        title: 'Job matches shown',
        description:
          "How many of Ari's matched roles you can see. Pro and Premium unlock every match Ari finds.",
        starter: 'Top 10',
        pro: 'All matches',
        premium: 'All matches',
      },
    ],
  },
  {
    category: 'One-page website',
    items: [
      {
        title: 'One-page website',
        description:
          'A sleek, professional one-page website to showcase your best work.',
        starter: true,
        pro: true,
        premium: true,
      },
      {
        title: 'Templates',
        description:
          'Choose from professionally designed templates for your one-page website.',
        starter: '8 templates',
        pro: 'All templates',
        premium: 'All templates',
      },
      {
        title: 'Links',
        description:
          'Add links to your work, socials, and other sites on your one-page website.',
        starter: '5 links',
        pro: '20 links',
        premium: '20 links',
      },
      {
        title: 'Remove Twine branding',
        description:
          'Hide the Twine branding footer on your one-page website for a fully custom look.',
        starter: false,
        pro: true,
        premium: true,
      },
    ],
  },
  {
    category: 'Pro Features',
    items: [
      {
        title: 'Pro badge',
        description:
          'Stand out from the crowd and increase your chance of being hired.',
        starter: false,
        pro: true,
        premium: true,
      },
      {
        title: 'Contact clients directly',
        description:
          'Win more work with the direct email, WhatsApp or phone number of clients who share their details.',
        starter: false,
        pro: true,
        premium: true,
      },
      {
        title: 'Priority support',
        description:
          'Any support request is handled with priority over Starter members.',
        starter: false,
        pro: true,
        premium: true,
      },
    ],
  },
];

const PricingBreakdownTable = () => {
  const [expanded, setExpanded] = useState({});
  const features = buildFeatures();
  const [visible, setVisible] = useState(false);

  const toggleExpand = (title) => {
    setExpanded((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const renderValue = (value) => {
    if (typeof value === 'boolean') {
      return value ? (
        <SvgIcon icon="tick" size="20" className={styles.checkIcon} />
      ) : (
        <span className={styles.dash}>-</span>
      );
    }
    return <p className={styles.featureText}>{value}</p>;
  };

  return (
    <VisibilitySensor
      partialVisibility
      delayedCall
      minTopValue={visible ? 0 : 100}
      active={!visible}
      onChange={(isVisible) => setVisible(isVisible)}
    >
      <div className={styles.container}>
        <h3 className={`${styles.header} ${visible ? '' : styles.hide}`}>
          Compare features
        </h3>
        {features.map((category) => (
          <>
            <div className={styles.categoryRow}>
              <h3 className={styles.categoryTitle}>{category.category}</h3>
            </div>
            <div className={styles.table}>
              <div className={styles.planTitles}>
                <div />
                <div className={styles.planTitle}>Starter</div>
                <div className={styles.planTitle}>Pro</div>
                <div className={styles.planTitle}>Premium</div>
              </div>
              <React.Fragment key={category.category}>
                {/* <div className={styles.categoryRow}>
                  <h3 className={styles.categoryTitle}>{category.category}</h3>
                </div> */}
                {category.items.map((item) => (
                  <div key={item.title} className={styles.featureWrapper}>
                    <div
                      className={styles.featureRow}
                      onClick={() => toggleExpand(item.title)}
                    >
                      <div className={styles.featureTitleCell}>
                        <SvgIcon
                          icon="chevron-down-narrow"
                          size="20"
                          className={`${styles.chevron} ${
                            expanded[item.title] ? styles.expanded : ''
                          }`}
                        />
                        <span className={styles.featureTitle}>
                          {item.title}
                        </span>
                      </div>
                      <div className={styles.planCell}>
                        {renderValue(item.starter)}
                      </div>
                      <div className={styles.planCell}>
                        {renderValue(item.pro)}
                      </div>
                      <div className={styles.planCell}>
                        {renderValue(item.premium)}
                      </div>
                    </div>
                    {expanded[item.title] && (
                      <div className={styles.descriptionRow}>
                        <div className={styles.description}>
                          <p>{item.description}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </React.Fragment>
            </div>
          </>
        ))}
      </div>
    </VisibilitySensor>
  );
};

export default PricingBreakdownTable;
